import { Chip } from "@mui/material";
import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase } from "utils/string";
import StyledSummaryCardLogo from "styles/StyledComponents/StyledSummaryCardLogo";
import { brandCheck, getOrderTypeDisplayLabel } from "utils/order";
import {
	ArrowContainer,
	ArrowForward,
	AssociatedOrdersData,
	CardContainer,
	CardContainerGrid,
	CardDataStyles,
	CardHeader
} from "../OrderDetailsHeader.styles";
import {
	DoorStyleText,
	ETATextOnly,
	FinishText,
	OrderDateText,
	OrderNumberText,
	PONumberText,
	SingleCardTruckText
} from "constants/text";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { formatDateToMMDDYYYY } from "utils/date";
import { OrderSummaryCardChip } from "components/OrderSummaryCard/OrderSummaryCard.styles.ts";
import { models } from "types/api/viewModels.ts";
import { getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";

interface IndividualLinkedOrderCardProps {
	goToOrderDetails: (orderId: string | number) => void;
	associatedOrder: models["OrderViewModel"];
}

const IndividualLinkedOrderCard = ({ goToOrderDetails, associatedOrder }: IndividualLinkedOrderCardProps) => {
	const orderGlobals = getSingleOrderGlobal(associatedOrder);

	return (
		<CardContainer
			onClick={() => goToOrderDetails(checkForAndReplaceNullEmptyWhitespace(associatedOrder?.orderId))}
			data-testid="linkedOrders-content"
		>
			<CardContainerGrid>
				<div>
					<CardHeader>
						<h2>{associatedOrder?.jobName}</h2>
						<Chip
							label={getOrderTypeDisplayLabel(associatedOrder?.orderType)}
							sx={{
								color: "var(--text-primary)",
								fontFamily: ["canada-type-gibson", "sans-serif"].join(","),
								fontWeight: "var(--font-weight-medium)",
								minWidth: "max-content",
								height: 24,
								margin: "0 0 0.5rem 0",
								"& .MuiChip-label": {
									fontSize: "var(--font-size-4)",
									letterSpacing: 0.46
								}
							}}
							data-testid="orderDetailsHeader-associatedOrders-chip"
						/>
					</CardHeader>

					{orderGlobals?.brand?.code && (
						<StyledSummaryCardLogo
							data-testid="linkedOrders-brand"
							parentBrand={orderGlobals.brand?.code}
							src={
								brandCheck(orderGlobals.brand?.code)
									? `/assets/manufacture_logos/${orderGlobals.brand?.code}.png`
									: "/assets/tandem_logos/cwg_logo.png"
							}
							alt={
								brandCheck(orderGlobals.brand?.code)
									? (orderGlobals.brand?.code ?? "")
									: "CabinetworksGroup Logo"
							}
						/>
					)}

					<AssociatedOrdersData>
						<CardDataStyles className="subtitle2">{PONumberText}</CardDataStyles>
						<span className="body2">
							{checkForAndReplaceNullEmptyWhitespace(associatedOrder?.poNumber)}
						</span>
					</AssociatedOrdersData>

					<AssociatedOrdersData>
						<CardDataStyles className="subtitle2">{OrderNumberText}</CardDataStyles>
						<span className="body2">
							{checkForAndReplaceNullEmptyWhitespace(associatedOrder?.orderNumber)}
						</span>
					</AssociatedOrdersData>

					<AssociatedOrdersData>
						<CardDataStyles className="subtitle2">{DoorStyleText}</CardDataStyles>
						<span className="body2">
							{checkForAndReplaceNullEmptyWhitespace(orderGlobals?.style?.description)}
						</span>
					</AssociatedOrdersData>

					<AssociatedOrdersData>
						<CardDataStyles className="subtitle2">{FinishText}</CardDataStyles>
						<span className="body2">
							{checkForAndReplaceNullEmptyWhitespace(orderGlobals?.finish?.description)}
						</span>
					</AssociatedOrdersData>

					{associatedOrder?.orderDate && (
						<AssociatedOrdersData>
							<CardDataStyles className="subtitle2">{OrderDateText}</CardDataStyles>
							<span className="body2">
								{formatDateToMMDDYYYY(new Date(String(associatedOrder.orderDate)))}
							</span>
						</AssociatedOrdersData>
					)}

					<AssociatedOrdersData>
						<CardDataStyles className="subtitle2">{ETATextOnly}</CardDataStyles>
						<span className="body2">{checkForAndReplaceNullEmptyWhitespace(associatedOrder?.eta)}</span>
					</AssociatedOrdersData>

					{associatedOrder?.shippingDetails?.truckNumber && (
						<AssociatedOrdersData>
							<CardDataStyles className="subtitle2">{SingleCardTruckText}</CardDataStyles>
							<span className="body2">{associatedOrder.shippingDetails.truckNumber}</span>
						</AssociatedOrdersData>
					)}

					{associatedOrder?.status && (
						<OrderSummaryCardChip
							label={convertToTitleCase(associatedOrder.status)}
							chipTitle={associatedOrder.status}
							sx={{
								width: "fit-content",
								height: "unset",
								marginBottom: !associatedOrder?.statusReason ? "unset !important" : "0.5rem !important"
							}}
						/>
					)}

					{associatedOrder?.statusReason && (
						<OrderSummaryCardChip
							label={convertToTitleCase(associatedOrder.statusReason)}
							chipTitle={associatedOrder.statusReason}
							sx={{
								width: "fit-content",
								height: "unset",
								marginBottom:
									associatedOrder?.status && associatedOrder?.statusReason
										? "unset !important"
										: "0.5rem !important"
							}}
						/>
					)}
				</div>

				<ArrowContainer>
					<ArrowForward>
						<ArrowForwardIosIcon />
					</ArrowForward>
				</ArrowContainer>
			</CardContainerGrid>
		</CardContainer>
	);
};

export default IndividualLinkedOrderCard;
